import classnames from 'classnames';
import { MdOutlineAltRoute, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { CopyToClipboard } from '@ui';
import { Accordion, AccordionItem } from '@ds';
import { CompactTripCheckpoint, TripStatusChip } from '@lp/features/RoutePlanner/components/GroundTrip';
import { CheckpointTypeEnum } from '@/api/apis/drive-service';
import { useDriveTripQuery } from '@/queries/drive-service';
import { GroundNotFeasibleWarning } from './GroundNotFeasibleWarning';

type GroundTripLegInfoProps = {
  tripId?: string;
  isFeasibleForDrive: boolean;
};

export const GroundTripLegInfo = ({ tripId, isFeasibleForDrive }: GroundTripLegInfoProps) => {
  const { data } = useDriveTripQuery(tripId);

  if (!data && isFeasibleForDrive) return null;

  const hasCheckpoints = !!data?.tripPlan.checkpoints.length;

  return (
    <div className="flex flex-col gap-1">
      {!isFeasibleForDrive && <GroundNotFeasibleWarning />}
      {!!data && (
        <Accordion className="p-0 w-auto! order-2 mt-1" isCompact showDivider={false}>
          <AccordionItem
            classNames={{
              heading: 'w-fit',
              trigger: classnames('cursor-pointer p-0 gap-1', { 'pointer-events-none': !hasCheckpoints }),
              indicator: classnames('text-black data-[open=true]:-rotate-180', { hidden: !hasCheckpoints }),
            }}
            title={
              <div className="flex gap-2 items-center">
                {data.isMergedTrip && <MdOutlineAltRoute size={16} />}
                <span className="text-s-sb text-base-10">{data.tripNumber}</span>
                <CopyToClipboard
                  text={data.tripNumber}
                  confirmationMessage="Trip number copied to clipboard"
                  iconSize={16}
                />
                <TripStatusChip status={data.status} />
              </div>
            }
            textValue={data.tripNumber}
            indicator={<MdOutlineKeyboardArrowDown size={24} />}
          >
            <div className="flex flex-col gap-1">
              {data.tripPlan.checkpoints.map((checkpoint) => {
                const key =
                  checkpoint.$type === CheckpointTypeEnum.PlannedCheckpointResponse
                    ? checkpoint.checkpointId
                    : checkpoint.modeData.waitStartTime;

                return (
                  <CompactTripCheckpoint
                    key={key}
                    data={checkpoint}
                    tripPassengers={data.passengers}
                    type="route-list"
                  />
                );
              })}
            </div>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};
